import { Box, CircularProgress, Drawer, useTheme } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useUpdateTaskChecklist } from 'api/aws/checklists/useUpdateTaskChecklist'
import { useEntitiesFromSearch } from 'api/search'
import { useSyncQueryCache } from 'api/aws/organizations_table/useSyncQueryCache'
import { useDownloadFile } from 'api/aws/s3/useDownloadFile'
import { useDeleteTaskImage } from 'api/aws/tasks/useDeleteTaskImage'
import AttachmentViewer from 'components/Comments/AttachmentViewer'
import { useProjectDrawerOpen, useTaskDrawerOpen } from 'context/DrawerProvider/useDrawerState'
import { useSnackbarContext } from 'context/SnackbarProvider/SnackbarProvider'
import { throttle } from 'lodash'
import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { Attachments, BatchUpdateItem, DrawerBaseProps, DrawerWrapperInjectedProps, Task } from 'types'
import { useUniversalNotify } from '../../api/aws/notifications/useUniversalNotify'
import { useBatchUpdateTaskItems } from '../../api/aws/tasks/useBatchUpdateTaskItems'
import { useBatchUpdateTasks } from '../../api/aws/tasks/useBatchUpdateTasks'
import { useSubtasksByTaskId } from '../../api/aws/tasks/useSubtasksById'
import { useUpdateTaskItems } from '../../api/aws/tasks/useUpdateTaskItems'
import { useLayoutContext } from '../../context/LayoutProvider/LayoutProvider'
import { useUserNotifications } from '../../context/NotificationProvider/useUserNotifications'
import ExpandCollapseBar from '../../theme/components/ExpandCollapseBar/ExpandCollapseBar'
import { boardViewMapping } from '../../utils/boardViewMapping'
import { useChangeView } from '../../utils/useChangeView'
import ExpandCollapseButton from '../ui/expand-collapse-button'
import DigitalInnerTaskRouting from './DigitalInnerTaskRouting'
import InnerTaskRouting from './InnerTaskRouting'
import PreviewTaskAttachments from './PreviewTaskAttachments'
import TaskDrawerHeader from './TaskDrawerHeader'
import { useTaskDrawerHandleFunctions } from './useTaskDrawerHandleFunctions'

type Props = DrawerBaseProps & DrawerWrapperInjectedProps
const TaskDrawer: React.FC<Props> = ({ closeDrawer, context, selectedRowId, getNeighbors }) => {
  const { PK, SK } = useParams()
  // const selectedRowId = useMemo(() => {
  //   return PK && SK ? `${PK}::${SK}` : null
  // }, [PK, SK])
  const { pinned } = useLayoutContext()
  const location = useLocation()
  const navigate = useNavigate()
  const [tabIndex, setTabIndex] = useState(0)
  const [expanded, setExpanded] = useState(true)
  const handleChangeView = useChangeView()
  const { showSnackbar } = useSnackbarContext()
  const queryClient = useQueryClient()
  const [viewerOpen, setViewerOpen] = useState(false)
  const [currentAttachments, setCurrentAttachments] = useState<Attachments[]>([])
  const [currentIndex, setCurrentIndex] = useState<number>(0)
  const { mutate: downloadFile } = useDownloadFile()
  const [taskDrawerWidth, setTaskDrawerWidth] = useState(null)
  const { organizations, deals, allTasks } = useEntitiesFromSearch()
  // const { task, isTaskLoading, isTaskError } = useTask(PK, SK)
  const task = useMemo(() => {
    if (!allTasks) return null
    return allTasks?.find(t => t?.SK === SK)
  }, [allTasks, SK])
  useEffect(() => console.log('task', task), [])
  const { data: subtasksById = [], isLoading: isSubtasksLoading } = useSubtasksByTaskId(task?.SK)
  const { subtasks = [], count = 0 } = subtasksById || {}
  const [localAddtlNotes, setLocalAddtlNotes] = useState(task?.description || task?.addtlNotes || '')
  const [localDetails, setLocalDetails] = useState(task?.details || '')
  const [localAssigner, setLocalAssigner] = useState<string | undefined>(undefined)
  const [checklistDraft, setChecklistDraft] = useState(task?.checklist || {})
  const [checklistChanges, setChecklistChanges] = useState<Record<string, any>>({})
  const [subtaskChanges, setSubtaskChanges] = useState<Record<string, any>>({})
  const [notifyUsers, setNotifyUsers] = useState<Array<Object[]>>([])
  const { notifyTaskAssignees } = useUserNotifications()
  const [isCommentOpen, setIsCommentOpen] = useState(false)
  const { prevId, nextId, index, total } = React.useMemo(
    () => (selectedRowId ? getNeighbors(selectedRowId) : { prevId: null, nextId: null, index: -1, total: 0 }),
    [selectedRowId, getNeighbors]
  )

  const splitRowId = (rowId: string) => {
    const [pk, sk] = rowId.split('::')
    return { pk, sk }
  }

  const getCurrentTabSegment = () => {
    const segs = ['main', 'messages', 'checklist', 'description', 'timeline'] as const
    return segs.find(s => location.pathname.includes('/' + s)) ?? 'main'
  }

  const rowIdToRoute = (rowId: string) => {
    const { pk, sk } = splitRowId(rowId)
    // Adjust base by context if needed
    const base = location.pathname.startsWith('/projects') ? 'projects' : 'teamtasks'
    return `/${base}/${pk}/${sk}`
  }

  const goPrev = React.useCallback(() => {
    if (!prevId) return
    navigate(`${rowIdToRoute(prevId)}/${getCurrentTabSegment()}`, { replace: true })
  }, [prevId, navigate])

  const goNext = React.useCallback(() => {
    if (!nextId) return
    navigate(`${rowIdToRoute(nextId)}/${getCurrentTabSegment()}`, { replace: true })
  }, [nextId, navigate])

  const organization = useMemo(() => {
    if (!organizations || !organizations?.length) return null
    return organizations?.find(o => o?.PK === PK)
  }, [organizations])
  const deal = useMemo(() => {
    if (!task || !deals || !deals?.length) return null
    const dealId = task?.deal_uuid || task?.dealId
    return deals?.find(d => d?.deal_uuid === dealId)
  }, [deals, task])
  const { mutate: updateTaskItem } = useUpdateTaskItems()
  const { mutate: batchUpdateTaskItems, isPending: isBatchUpdating } = useBatchUpdateTaskItems()
  const { mutate: deleteTaskImage } = useDeleteTaskImage()
  const typeUpdateBuilderRef = React.useRef<null | (() => BatchUpdateItem[])>(null)
  const paperRef = React.useRef<HTMLDivElement | null>(null)
  const { mutate: batchUpdateParentTask } = useBatchUpdateTasks()
  const [localAssignees, setLocalAssignees] = useState(task?.assignees || [])
  const openProjectDrawer = useProjectDrawerOpen()
  const openTeamTasksDrawer = useTaskDrawerOpen()
  const isDrawerOpen = context === 'projects' ? openProjectDrawer : openTeamTasksDrawer
  const theme = useTheme()
  const { notify } = useUniversalNotify()
  const {
    handleSaveAssignees,
    handleUpdateMasterStatus,
    handleUpdateSubtaskStatus,
    handleUpdateActiveAssignee,
    handleSaveTaskNotes,
    handleUpdateSubtaskDateRange,
    isStaleData,
    setIsStaleData,
  } = useTaskDrawerHandleFunctions()
  const { mutate: updateChecklist } = useUpdateTaskChecklist()
  const { syncTasksCacheAfterBatchUpdate } = useSyncQueryCache()

  // Recompute progress from a list
  const computeProgress = (list = []) => ({
    total: list.length,
    done: list.filter(i => i?.done).length,
  })

  //////////////////////// Edit / Save Mode(s) ////////////////////////////////
  const [editingChecklist, setEditingChecklist] = useState(false)
  const [draftChecklist, setDraftChecklist] = useState(task?.checklist || [])
  const lastTaskRef = React.useRef<Task | null>(null)
  const stableTask = task ?? lastTaskRef.current
  useEffect(() => {
    if (task) lastTaskRef.current = task
  }, [task])

  useEffect(() => {
    if (!task) return
    setLocalAssignees(task.assignees ?? [])
    // only when identity changed:
  }, [task?.PK, task?.SK])

  useEffect(() => {
    if (!task?.checklist) return
    setDraftChecklist(task.checklist)
  }, [task?.PK, task?.SK])

  const onToggleDraft = item => {
    setDraftChecklist(prev => prev.map(i => (i.id === item.id ? { ...i, done: !i.done } : i)))
  }
  const handleSaveAll = () => {
    const batch: BatchUpdateItem[] = []

    // 1) type fields (from child)
    const typeUpdates = typeUpdateBuilderRef.current?.() ?? []
    batch.push(...typeUpdates)

    // 2) addtlNotes (only if changed)
    if ((localAddtlNotes || '') !== (task?.addtlNotes || '')) {
      batch.push({
        primaryKey: 'PK',
        sortKey: 'SK',
        tableName: 'TASKS_TABLE',
        itemId: task.PK,
        itemSortKey: task.SK,
        fieldName: 'addtlNotes',
        newValue: localAddtlNotes,
      })
    }

    // 3) details (only if changed)
    if ((localDetails || '') !== (task?.details || '')) {
      batch.push({
        primaryKey: 'PK',
        sortKey: 'SK',
        tableName: 'TASKS_TABLE',
        itemId: task.PK,
        itemSortKey: task.SK,
        fieldName: 'details',
        newValue: localDetails,
      })
    }

    if (batch.length === 0) {
      setEditingChecklist(false)
      return
    }

    const optimisticNext = applyBatchToTask(task, batch)
    console.log('batch', batch)
    console.log('optimisticNext', optimisticNext)
    batchUpdateTaskItems(batch, {
      // 👇 optimistic cache update
      // onMutate: async () => {
      //   const prevLists = queryClient.getQueriesData<Task[]>({
      //     predicate: q => Array.isArray(q.queryKey) && q.queryKey[0] === 'tasks_table',
      //   })
      //   const prevTask = queryClient.getQueryData<Task>(['task', task.PK, task.SK])

      //   // pass prev cache for rollback
      //   return { prevLists, prevTask }
      // },

      // 👇 rollback if API fails
      // onError: (_err, _vars, ctx) => {
      //   if (!ctx) return
      //   for (const [key, data] of ctx.prevLists ?? []) {
      //     queryClient.setQueryData(key as any, data)
      //   }
      //   if (ctx.prevTask) {
      //     queryClient.setQueryData(['task', task.PK, task.SK], ctx.prevTask)
      //   }
      // },

      // 👇 keep or reconcile with server, if it returns data
      onSuccess: _data => {
        syncTasksCacheAfterBatchUpdate(queryClient, [optimisticNext])
        // syncTaskEverywhere(queryClient, optimisticNext)
        queryClient.invalidateQueries({ queryKey: ['task', PK, SK] })
        // If your endpoint returns a canonical task, you can reconcile here:
        // const serverTask = ...
        // syncTaskEverywhere(queryClient, { ...optimisticNext, ...serverTask })
      },

      onSettled: () => {
        setEditingChecklist(false)
      },
    })
  }

  const saveDraftChecklist = () => {
    const nextProgress = {
      total: draftChecklist.length,
      done: draftChecklist.filter(i => i.done).length,
    }
    const updatedTaskData = { ...task, checklist: draftChecklist, progress: nextProgress }

    updateChecklist(
      {
        PK: task.PK,
        SK: task.SK,
        checklist: draftChecklist,
        progress: nextProgress,
      },
      {
        onSuccess: () => {
          if ((localAddtlNotes || '') !== (task?.addtlNotes || '')) handleSaveTaskNotes(localAddtlNotes, task)

          syncTasksCacheAfterBatchUpdate(queryClient, [updatedTaskData])
        },
      }
    )
    setEditingChecklist(false)
  }
  /////////////////////////////////////////////////////////////////////////////

  /////////////// Track Status Changes ////////////////////
  useEffect(() => {
    if (task?.SK && task?.SK?.startsWith('TASK)') && subtasks?.length > 0) {
      const counts = subtasks.reduce(
        (acc, st) => {
          const status = st.masterStatus
          if (status === 'Done') acc.done++
          else if (status === 'In Progress') acc.inProgress++
          else if (status === 'Needs Review') acc.needsReview++
          else if (status === 'Need More Info') acc.needMoreInfo++
          else if (status === 'On Hold') acc.onHold++
          else acc.notStarted++
          return acc
        },
        {
          done: 0,
          inProgress: 0,
          onHold: 0,
          notStarted: 0,
          needsReview: 0,
          needMoreInfo: 0,
        }
      )

      const total = subtasks.length
      const updatedSummary = { ...counts, total }

      const hasChanged = JSON.stringify(task.progressSummary || {}) !== JSON.stringify(updatedSummary)
      if (!PK || !SK) return console.error('No Task PK or SK found.')
      if (hasChanged) {
        updateTaskItem({
          updates: [
            {
              primaryKey: 'PK',
              sortKey: 'SK',
              tableName: 'TASKS_TABLE',
              pkValue: task.PK || PK,
              skValue: task.SK || SK,
              fieldName: 'progressSummary',
              newValue: updatedSummary,
            },
          ],
        })
      }
    }
  }, [subtasks])

  ////////////////////////////////////////////////////////////////////////

  useEffect(() => {
    if (Object.keys(subtaskChanges).length > 0) {
      console.log('[📦 Subtask Changes]', subtaskChanges)
    }
  }, [subtaskChanges])
  useEffect(() => {
    if (task) {
      setLocalAssignees(task.assignees || [])
    }
  }, [task])

  const drawerTransitionDuration = 300
  useEffect(() => {
    if (task?.checklist) {
      setChecklistDraft(task.checklist)
      setNotifyUsers([])
      setChecklistChanges({}) // 🧼 clear on new task load
    }
  }, [task?.checklist])

  const logWidth = throttle(width => {
    console.log(`Current drawer width: ${width}px`)
  }, 1000)

  const handleMouseMove = e => {
    const newWidth = window.innerWidth - e.clientX
    const minWidth = 200
    const maxWidth = window.innerWidth * 0.8
    const adjustedWidth = Math.min(Math.max(minWidth, newWidth), maxWidth)
    setTaskDrawerWidth(adjustedWidth)
    logWidth(adjustedWidth)
  }

  const handleMouseUp = () => {
    document.body.style.removeProperty('user-select')
    document.removeEventListener('mousemove', handleMouseMove)
    document.removeEventListener('mouseup', handleMouseUp)
  }

  useEffect(() => {
    if (task) {
      setLocalAddtlNotes(task?.description || task?.addtlNotes || '')
      setLocalDetails(task?.details || '')
    }
  }, [task])
  const isLoading = isSubtasksLoading

  const TAB_SEGMENTS = ['main', 'messages', 'checklist', 'description', 'timeline'] as const

  const handleNavigate = (taskRoute: string, stayOpen = false) => {
    const route = taskRoute.includes('nco')
      ? '/nco'
      : taskRoute.includes('teamtasks')
      ? '/teamtasks'
      : taskRoute.includes('projects')
      ? '/projects'
      : taskRoute.includes('organizations')
      ? `/organizations/${organization?.PK}/details`
      : '/mtb'

    const newView = boardViewMapping[route]
    if (newView) handleChangeView({ newView })

    const baseRoute =
      taskRoute.startsWith('/teamtasks') || taskRoute.startsWith('/projects') || taskRoute.startsWith('/nco')

    if (!baseRoute) closeDrawer()

    // Only append tab segment for task-related routes, not for organizations
    if (taskRoute.includes('organizations')) {
      navigate(taskRoute)
    } else {
      const seg = getCurrentTabSegment() // 👈 preserve current tab
      navigate(`${taskRoute}/${seg}`) // 👈 append it
    }
  }

  // Update localAddtlNotes on editor changes (only update state; no saving yet)
  const handleFieldUpdates = (field, newValue) => {
    if (field === 'addtlNotes') {
      setLocalAddtlNotes(newValue)
    } else if (field === 'assignees') {
      setLocalAssignees(newValue)
    } else if (field === 'details') {
      setLocalDetails(newValue)
    } else if (field === 'assigner') {
      setLocalAssigner(newValue)
    }
  }

  const handleSaveAssigner = (assignerId: string | null) => {
    if (!task) return
    const batch: BatchUpdateItem[] = [
      {
        primaryKey: 'PK',
        sortKey: 'SK',
        tableName: 'TASKS_TABLE',
        itemId: task.PK,
        itemSortKey: task.SK,
        fieldName: 'assigner',
        newValue: assignerId,
      },
    ]
    const optimisticNext = { ...task, assigner: assignerId }
    batchUpdateTaskItems(batch, {
      onSuccess: () => {
        syncTasksCacheAfterBatchUpdate(queryClient, [optimisticNext])
        setLocalAssigner(undefined) // Reset local state after save
        showSnackbar('Assigner saved successfully', 'success')
      },
    })
  }

  const handleClose = () => {
    if (isStaleData) {
      console.log('Stale Task data detected, invalidating....')
      queryClient.invalidateQueries({ queryKey: ['teamTasks', {}] })
    }
    closeDrawer()
    setChecklistChanges({})
    setSubtaskChanges({})
    setIsStaleData(false)
  }
  function applyBatchToTask(task: Task, batch: BatchUpdateItem[]): Task {
    let next: Task = {
      ...task,
      extraFields: { ...(task.extraFields ?? {}) },
    }

    for (const u of batch) {
      if (u.itemSortKey !== task.SK) continue

      if (u.fieldName === 'extraFields' && u.newValue && typeof u.newValue === 'object') {
        next = { ...next, extraFields: { ...next.extraFields, ...u.newValue } }
      } else {
        ;(next as any)[u.fieldName] = u.newValue
      }
    }

    // If checklist changed, recompute progress like you do elsewhere
    const checklistUpdate = batch.find(b => b.fieldName === 'checklist')
    if (checklistUpdate && Array.isArray(checklistUpdate.newValue)) {
      const list = checklistUpdate.newValue as Array<{ done?: boolean }>
      const total = list.length
      const done = list.filter(i => i?.done).length
      next = { ...next, progress: { total, done } }
    }

    return next
  }
  const onStartEdit = () => {
    setEditingChecklist(true)
  }
  const onStopEdit = () => {
    setEditingChecklist(false)
    setDraftChecklist(task?.checklist || [])
  }

  ////////////////////// Preview Attachments ///////////////////////////
  const handleOpenViewer = (attachments: Attachments[], index: number) => {
    setCurrentAttachments(attachments)
    setCurrentIndex(index)
    setViewerOpen(true)
  }
  const handleDownload = (s3Url: string) => {
    const key = s3Url.split('.amazonaws.com/')[1]
    downloadFile(key, {
      onSuccess: (url: string) => {
        const link = document.createElement('a')
        link.href = url
        link.download = key.split('/').pop() || 'downloaded_image'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      },
      onError: (error: any) => {
        console.error('Error fetching presigned URL:', error)
      },
    })
  }

  const handleDeleteImage = (url: string) => {
    if (!task) return
    deleteTaskImage({ task, url })
  }
  useEffect(() => {
    if (!isDrawerOpen) return

    // ---- helpers ----
    const isClickInsideDrawer = (node: Node) => !!paperRef.current && paperRef.current.contains(node)

    const isGridRoot = (el: Element | null) => !!el && !!el.closest('.global-data-grid')

    const isRowHeaderOrCell = (el: Element | null) =>
      !!el &&
      !!el.closest('.MuiDataGrid-row, .MuiDataGrid-cell, .MuiDataGrid-columnHeaders, .MuiDataGrid-toolbarContainer')

    // ---- handlers ----
    const onEsc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || viewerOpen || isCommentOpen) return
      e.preventDefault()
      e.stopPropagation()

      // If you also want to navigate back to the base route, do it here:
      // const first = location.pathname.split('/')[1] || '';
      // const base = first ? `/${first}` : '/';
      // if (location.pathname !== base) navigate(base);

      handleClose()
    }

    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Element | null

      // ignore clicks inside the drawer
      if (target && isClickInsideDrawer(target)) return

      // close only when clicking the empty grid area
      if (target && isGridRoot(target) && !isRowHeaderOrCell(target)) {
        handleClose()
      }
    }

    // capture phase so we beat nested handlers
    window.addEventListener('keydown', onEsc, true)
    window.addEventListener('click', onDocClick, true)

    return () => {
      window.removeEventListener('keydown', onEsc, true)
      window.removeEventListener('click', onDocClick, true)
    }
  }, [isDrawerOpen, handleClose /*, location.pathname, navigate (if you uncomment navigation) */])
  return (
    <Drawer
      anchor="right"
      open={isDrawerOpen}
      onClose={handleClose}
      variant="persistent"
      hideBackdrop
      ModalProps={{
        keepMounted: true,
        slotProps: {
          backdrop: {
            transitionDuration: 0, // optional: stops flicker on row changes
            sx: {
              // make the backdrop sit below the special grid cell/row
              // zIndex: 1100,
            },
          },
        },
      }}
      PaperProps={{
        ref: paperRef,
        sx: {
          display: 'flex',
          flexDirection: 'column',
          width: '79.5%',
          height: '100dvh',
          pt: pinned ? '55px' : 0,
          overflow: 'hidden',
          bgcolor: theme.palette.background.section, // <- match footer bg
          boxShadow: '10px 21px 43px rgba(0, 0, 0, 0.2)',
          zIndex: theme => theme.zIndex.drawer, // ~1200 by default
        },
      }}
      // hideBackdrop
    >
      {/* SCROLLABLE CONTENT AREA */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0, // important so overflow works in flex item
          // overflowY: 'auto',
          bgcolor: theme.palette.background.section,
          scrollbarGutter: 'stable both-edges', // avoids layout jiggle
        }}
      >
        <ExpandCollapseButton
          expanded={expanded}
          setExpanded={setExpanded}
          displayElement={<ExpandCollapseBar entity={stableTask} isExpanded={expanded} onClose={handleClose} />}
        >
          <TaskDrawerHeader
            task={stableTask}
            organization={organization}
            deal={deal ?? null}
            onUpdateMasterStatus={handleUpdateMasterStatus}
            onChange={(field, value) => handleFieldUpdates(field, value)}
            localAssignees={localAssignees}
            onSaveAssignees={handleSaveAssignees}
            isStaleData={isStaleData}
            localAssigner={localAssigner}
            onSaveAssigner={handleSaveAssigner}
          />
        </ExpandCollapseButton>

        {isLoading ? (
          <CircularProgress />
        ) : task && !task?.isMarketing ? (
          <Box sx={{ px: 1, pb: 2 }}>
            <InnerTaskRouting
              task={stableTask}
              organization={organization}
              subtasks={subtasks}
              isTaskLoading={isLoading}
              onNavigate={handleNavigate}
              onChange={handleFieldUpdates}
              onSaveAll={handleSaveAll}
              onSaveChecklist={saveDraftChecklist}
              onStartEdit={onStartEdit}
              onStopEdit={onStopEdit}
              isUpdating={isBatchUpdating}
              addtlNotes={localAddtlNotes}
              expanded={expanded}
              items={editingChecklist ? draftChecklist : task?.checklist}
              editing={editingChecklist}
              onToggle={editingChecklist ? onToggleDraft : undefined}
              registerTypeUpdateBuilder={fn => {
                typeUpdateBuilderRef.current = fn
              }}
              selectedRowId={selectedRowId}
              getNeighbors={getNeighbors}
              onPrev={prevId ? goPrev : undefined}
              onNext={nextId ? goNext : undefined}
              navState={{ index, total, hasPrev: !!prevId, hasNext: !!nextId }}
              isCommentOpen={isCommentOpen}
              setIsCommentOpen={setIsCommentOpen}
              localDetails={localDetails}
            />
          </Box>
        ) : (
          <Box sx={{ px: 1, pb: 2 }}>
            <DigitalInnerTaskRouting
              task={stableTask}
              organization={organization}
              subtasks={subtasks}
              isTaskLoading={isLoading}
              onNavigate={handleNavigate}
              onChange={handleFieldUpdates}
              onSaveAll={handleSaveAll}
              onSaveChecklist={saveDraftChecklist}
              onStartEdit={onStartEdit}
              onStopEdit={onStopEdit}
              isUpdating={isBatchUpdating}
              addtlNotes={localAddtlNotes}
              expanded={expanded}
              items={editingChecklist ? draftChecklist : task?.checklist}
              editing={editingChecklist}
              onToggle={editingChecklist ? onToggleDraft : undefined}
              registerTypeUpdateBuilder={fn => {
                typeUpdateBuilderRef.current = fn
              }}
              selectedRowId={selectedRowId}
              getNeighbors={getNeighbors}
              onPrev={prevId ? goPrev : undefined}
              onNext={nextId ? goNext : undefined}
              navState={{ index, total, hasPrev: !!prevId, hasNext: !!nextId }}
              isCommentOpen={isCommentOpen}
              setIsCommentOpen={setIsCommentOpen}
              localDetails={localDetails}
            />
          </Box>
        )}
      </Box>
      {/* TRUE FOOTER (NON-SCROLLING) */}
      <PreviewTaskAttachments
        task={stableTask}
        onDeleteFile={handleDeleteImage}
        handleDownload={handleDownload}
        onOpenViewer={handleOpenViewer}
      />

      {viewerOpen && (
        <AttachmentViewer
          open={viewerOpen}
          attachments={currentAttachments}
          currentIndex={currentIndex}
          onClose={() => setViewerOpen(false)}
          onNavigate={dir => {
            if (dir === 'prev') setCurrentIndex(i => Math.max(0, i - 1))
            else setCurrentIndex(i => Math.min(currentAttachments.length - 1, i + 1))
          }}
        />
      )}
    </Drawer>
  )
}

export default TaskDrawer
